import {
  Component,
  Input,
  Output,
  OnInit,
  SimpleChanges,
  ChangeDetectorRef,
  OnChanges,
  AfterViewInit,
  EventEmitter,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  OrderableQualifier,
  OrderedIngredientService,
  ImageAlternativeType,
  OrderableIngredient,
  OrderedIngredient,
  IngredientChoice,
  OrderedChoice,
  StoreConfig,
  AuditService,
} from 'ngx-web-api';
import { Observable, noop, of } from 'rxjs';
import { Animations } from '../../../../../animations';
import { IngredientSelection } from '../../../../../domain/ingredient-selection';
import { MenuWrapperService } from '../../../../../core/services/menu-wrapper.service';
import { ThemeService } from '../../../../../core/services/theme.service';
import { Theme } from '../../../../../domain/theme.model';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons/faQuestionCircle';
import { filter, take } from 'rxjs/operators';
import { ExtraIngredientUpsellService } from 'app/core/services/extra-ingredient-upsell.service';
import { ExtraUpsell } from 'app/domain/extra-upsell';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { FocusService } from 'app/focus.service';
import { ViewportService } from 'app/core/services/viewport.service';
import { ModalService } from 'app/core/services/modal.service';
import { ConfirmationModalContext } from 'app/domain/confirmation-modal-context';
import { TranslateService } from '@ngx-translate/core';
import { ItemEditorService } from 'app/core/services/item-editor.service';
import { BreakpointService } from 'app/core/services/breakpoint.service';

@Component({
  selector: 'fts-ingredient-card',
  templateUrl: './ingredient-card.component.html',
  styleUrls: ['./ingredient-card.component.scss'],
  animations: [Animations.scaleX],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IngredientCardComponent implements OnInit, OnChanges, AfterViewInit {
  @Input()
  ingredient: OrderableIngredient;
  @Input()
  orderedIngredient: OrderedIngredient;
  @Input()
  itemSize: string;
  @Input()
  singleSelectionChoiceSelectedIngredient: OrderedIngredient;
  @Input()
  choice: IngredientChoice;
  @Input()
  disabled: boolean;
  @Input()
  isInModal: boolean;
  @Input()
  ingredientIndex: number;
  @Input()
  orderedChoice: OrderedChoice;
  @Input()
  storeConfig: StoreConfig;
  @Input()
  inItemEditorModal: boolean;
  @Input()
  hasTags: any;

  @Output()
  ingredientSelectedChange: EventEmitter<OrderableIngredient> = new EventEmitter();
  @Output()
  ingredientHalfChange: EventEmitter<IngredientSelection> = new EventEmitter();
  @Output()
  ingredientQualifierChange: EventEmitter<IngredientSelection> = new EventEmitter();
  @Output()
  tagClicked: EventEmitter<string> = new EventEmitter<string>();

  imgSrc: string;
  shouldShowQualifiers = false;
  qualifierGroups: Array<OrderableQualifier[]>;
  calories: number;
  servingLabel: string;
  selectionColor: string;
  primaryBgColor: string;
  ingredientPrice: number;
  faQuestionCircle = faQuestionCircle;
  faCaretDown = faCaretDown;
  faCaretUp = faCaretUp;
  singleDefaultName: string;
  sideOnly = false;

  isDisabled: boolean;
  isDisabledQualifier: boolean;
  isUnavailable: boolean;
  isDefault: boolean;
  isOrdered: boolean;
  availableSizes: string;
  disableWrapperHeight: string;

  public theme$: Observable<Theme>;
  public extraUpsell: ExtraUpsell;

  @ViewChild('popoverRef', { static: false })
  public popoverRef: ElementRef;
  @ViewChild('qualifierWrapper', { static: false })
  private qualifierWrapper: ElementRef;

  constructor(
    private orderedIngredientService: OrderedIngredientService,
    private cdRef: ChangeDetectorRef,
    private themeService: ThemeService,
    private menuService: MenuWrapperService,
    private extraUpsellService: ExtraIngredientUpsellService,
    private elRef: ElementRef,
    private focusService: FocusService,
    public viewportService: ViewportService,
    private modalService: ModalService,
    private translate: TranslateService,
    private editorService: ItemEditorService,
    private auditService: AuditService,
    public breakpointSerivce: BreakpointService
  ) {
    this.theme$ = this.themeService.theme;
    this.extraUpsellService.extraUpsell$
      .pipe(
        filter(upsell => upsell.choice === this.choice.name && upsell.ingredient === this.ingredient.name),
        take(1)
      )
      .subscribe(upsell => {
        this.extraUpsell = upsell;
        this.cdRef.detectChanges();
        setTimeout(() => {
          if (this.popoverRef && !this.extraUpsellService.isPopupOpen()) {
            this.extraUpsellService.setPopupOpen(true);
            this.focusService.activateTrapAndScrollToElement(this.popoverRef, 48); // 48px is the height of the fixed item editor header
          }
        }, 50);
      }, noop);

    this.extraUpsellService.documentClickEvent$
      .pipe(filter(event => !this.elRef.nativeElement.contains(event.target)))
      .subscribe(() => this.denyExtraUpsell(), noop);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['itemSize']) {
      this.calcCalories();
    }
    if (changes['orderedIngredient']) {
      this.calcIngredientPrice();
      if (this.orderedIngredient && this.orderedIngredient.qualifiers && this.orderedIngredient.qualifiers.length > 0) {
        this.shouldShowQualifiers = true;
        this.groupQualifiers();
      } else {
        this.shouldShowQualifiers = false;
      }
    }

    if (changes['disabled'] || changes['itemSize'] || changes['choice']) {
      if (!!this.itemSize) {
        this.calcUnavailable();
      }
      if (!!this.choice) {
        this.calcDisabled();
        this.cdRef.detectChanges();
      }
    }

    this.calcOrdered();
  }

  ngOnInit() {
    this.sideOnly = this.editorService.hasSideQualsOnly(this.ingredient.qualifiers);
    const cardImage = this.menuService.getPicture(this.ingredient.pictures, ImageAlternativeType.ORIGINAL, null);
    this.imgSrc = !!cardImage ? cardImage.src : undefined;
    this.singleDefaultName =
      this.choice &&
      this.choice.needsSingleIngredientSelection &&
      (this.choice.ingredients.find(i => i.acknowledgeRemovedDefaults) || { name: null }).name;
    if (this.ingredient?.qualifiers?.length) {
      this.groupQualifiers();
    }
  }

  ngAfterViewInit() {
    this.calcCalories();
    if (!!this.choice) {
      this.calcDisabled();
      this.cdRef.detectChanges();
    }
    this.cdRef.detectChanges();
  }

  calcOrdered() {
    this.isOrdered = !!this.orderedIngredient;
    this.cdRef.detectChanges();
  }

  calcDisabled() {
    this.isDisabledQualifier = this.disabled || this.isUnavailable;
    if (!this.isDisabledQualifier) {
      const disableWrapperClientHeight = this.qualifierWrapper?.nativeElement?.clientHeight;
      if (disableWrapperClientHeight > 0) {
        this.disableWrapperHeight = `${disableWrapperClientHeight + 3}px`;
      }
    }
    this.isDisabled = (this.choice.defaultsNonRemovable && this.ingredient.isDefault) || this.disabled || this.isUnavailable;
    if (this.isUnavailable && this.storeConfig.showSizeSpecificIngredients) {
      this.availableSizes = '';
      this.availableSizes = this.ingredient.sizePrices.map(sp => sp.size).join(', ');
    }
  }

  calcUnavailable() {
    this.isUnavailable = !this.ingredient.getSizePrice(this.itemSize);
  }

  toggleIngredient() {
    const singleDefaultOrdered = this.orderedChoice.ingredients.find(i => i.ingredient === this.singleDefaultName);
    if (
      (this.ingredient.acknowledgeRemovedDefaults && this.isOrdered) ||
      (!!this.singleDefaultName && this.singleDefaultName !== this.ingredient.name && !this.isOrdered && !!singleDefaultOrdered)
    ) {
      this.acknowledgeModal$().subscribe(result => {
        if (!result) {
          return;
        }
        if (this.extraUpsell) {
          this.denyExtraUpsell();
        }
        if (this.isDisabled) {
          return;
        }

        this.ingredientSelectedChange.emit(this.ingredient);
        this.calcCalories(true);
      });
      return;
    }

    if (this.extraUpsell) {
      this.denyExtraUpsell();
    }
    if (this.isDisabled) {
      return;
    }

    this.ingredientSelectedChange.emit(this.ingredient);
    this.calcCalories(true);
  }

  selectHalf(left: boolean, right: boolean) {
    if (
      (this.isOrdered && !left && !right && !this.orderedIngredient.isLeftHalf && !this.orderedIngredient.isRightHalf) ||
      (left && this.orderedIngredient.isLeftHalf) ||
      (right && this.orderedIngredient.isRightHalf)
    ) {
      this.toggleIngredient();
      return;
    }
    const isHalfEvent = this.checkHalfEvent(left, right);
    if (
      this.ingredient.acknowledgeRemovedDefaults &&
      this.isOrdered &&
      isHalfEvent &&
      !this.orderedIngredient.isRightHalf &&
      !this.orderedIngredient.isLeftHalf
    ) {
      this.acknowledgeModal$(true).subscribe(result => {
        if (!result) {
          return;
        }
        this.ingredientHalfChange.emit({
          ingredient: this.ingredient,
          isLeftHalf: left,
          isRightHalf: right,
        });
        this.calcCalories();
      }, noop);
      return;
    }

    if (this.isDisabled) {
      return;
    }

    this.ingredientHalfChange.emit({
      ingredient: this.ingredient,
      isLeftHalf: left,
      isRightHalf: right,
    });
    this.calcCalories();
  }

  toggleQualifier(qualifier: OrderableQualifier, extra = false) {
    if (this.shouldShowQualifiers) {
      const orderedIngredientQual = this.orderedIngredient.qualifiers?.find(qual => (qual = qualifier.name));
      if (qualifier.name !== orderedIngredientQual && !!orderedIngredientQual) {
        setTimeout(() => this.focusService.applyFocus(`qualifierInfoGroup-${this.ingredient?.name}`));
      }
    }
    this.ingredientQualifierChange.emit({
      ingredient: this.ingredient,
      qualifier: qualifier,
      isLeftHalf: this.orderedIngredient.isLeftHalf,
      isRightHalf: this.orderedIngredient.isRightHalf,
      isExtraUpsell: extra,
    });
    this.calcCalories();

    if (extra) {
      this.dismissUpsellPopup();
      this.shouldShowQualifiers = true;
      this.groupQualifiers();
    }
  }

  toggleShowQualifiers() {
    setTimeout(() => this.focusService.applyFocus(`qualifierInfoGroup-${this.ingredient?.name}`));
    if (this.qualifierGroups[0]?.length === 1) {
      this.toggleQualifier(this.qualifierGroups[0][0]);
      return;
    }
    if (this.isDisabledQualifier) {
      return;
    }
    this.shouldShowQualifiers = !this.shouldShowQualifiers;
    this.groupQualifiers();
  }

  groupQualifiers(): void {
    const groups = {};
    (this.ingredient.qualifiers || []).forEach(qualifier => {
      groups[qualifier.type] = groups[qualifier.type] || [];
      groups[qualifier.type].push(qualifier);
    });

    const sortedGroups = {};
    Object.keys(groups)
      .sort()
      .forEach(key => (sortedGroups[key] = groups[key]));
    this.qualifierGroups = Object.keys(sortedGroups).map(key => sortedGroups[key]);
  }

  ingredientDescriptionClicked(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  calcIngredientPrice() {
    this.ingredientPrice = (this.orderedIngredient && this.orderedIngredient.price) || this.getSelectedSizePrice();
  }

  denyExtraUpsell() {
    this.dismissUpsellPopup();
    this.extraUpsellService.denyExtraUpsell();
  }

  onPictureSet() {
    this.cdRef.detectChanges();
  }

  onIngredientClick() {
    if (this.isUnavailable && this.storeConfig.showSizeSpecificIngredients) {
      const modalTitle = this.translate.instant('component.item_editor.choice.ingredient.unavailable_size_ingredients.modal_title');
      const modalBody = this.translate.instant('component.item_editor.choice.ingredient.unavailable_size_ingredients.modal_body', {
        ingredientName: this.ingredient.name,
        size: this.itemSize,
        availableSizes: this.availableSizes,
      });
      this.auditService.createAudit(() => `${modalTitle} modal opened`);
      this.modalService.openWarningNotificationModal(modalTitle, modalBody, true).subscribe(result => {
        if (result === null) {
          this.auditService.createAudit(() => `"OK" button has been clicked`);
        }
      });
    }
  }

  private dismissUpsellPopup() {
    this.extraUpsell = null;
    this.cdRef.detectChanges();
    this.extraUpsellService.setPopupOpen(false);
  }

  private acknowledgeModal$(half = false): Observable<any> {
    const context = new ConfirmationModalContext();
    context.textOnly = false;
    context.reverseButtons = true;
    if (half) {
      context.mainContent = `
      <h3 class="text-uppercase">${this.translate.instant('component.item_editor.choice.ingredient.remove_default.sure')}</h3>
      <div class="font-size-larger">
        <div>${this.translate.instant('component.item_editor.choice.ingredient.remove_default.did_half')}</div>
        <div class="text-uppercase text-danger fw-600">
          ${this.translate.instant('component.item_editor.choice.ingredient.remove_default.on_half', {
            name: this.singleDefaultName || this.ingredient.name,
          })}
        </div>
        <div>${this.translate.instant('component.item_editor.choice.ingredient.remove_default.of')}</div>
      </div>
    `;
    } else {
      context.mainContent = `
      <h3 class="text-uppercase">${this.translate.instant('component.item_editor.choice.ingredient.remove_default.sure')}</h3>
      <div class="font-size-larger">
        <div>${this.translate.instant('component.item_editor.choice.ingredient.remove_default.did_whole')}</div>
        <div class="text-uppercase text-danger fw-600">
          ${this.translate.instant('component.item_editor.choice.ingredient.remove_default.remove', {
            name: this.singleDefaultName || this.ingredient.name,
          })}
        </div>
        <div>${this.translate.instant('component.item_editor.choice.ingredient.remove_default.from')}</div>
      </div>
    `;
    }
    context.submitBtnTitle = this.translate.instant('component.item_editor.choice.ingredient.remove_default.confirm');
    context.dismissBtnTitle = this.translate.instant('component.item_editor.choice.ingredient.remove_default.dismiss');
    context.submitObservable = of(true);
    return this.modalService.openConfirmationModal(context, null, true);
  }

  private checkHalfEvent(left: boolean, right: boolean) {
    return (
      this.ingredient.allowHalves &&
      ((this.orderedIngredient.isLeftHalf && !left) ||
        (this.orderedIngredient.isRightHalf && !right) ||
        (!this.orderedIngredient.isLeftHalf && !this.orderedIngredient.isRightHalf && (left || right)))
    );
  }

  private calcCalories(actionToggle: boolean = false): void {
    const selectedSizePrice = this.ingredient.sizePrices.find(s => s.size === this.itemSize) || this.ingredient.sizePrices[0];

    if (!this.orderedIngredient) {
      this.calories = this.orderedIngredientService.getCalories(this.ingredient, selectedSizePrice.size);
    } else {
      this.calories = this.orderedIngredientService.getCalories(
        this.ingredient,
        this.itemSize,
        actionToggle ? false : this.orderedIngredient.isLeftHalf || this.orderedIngredient.isRightHalf,
        actionToggle ? [] : this.orderedIngredient.qualifiers
      );
    }

    if (this.ingredient.determinesRecipe) {
      this.servingLabel = selectedSizePrice.servingsLabel || undefined;
    }
    this.calcIngredientPrice();
    this.cdRef.detectChanges();
  }

  private getSelectedSizePrice(): number {
    const selectedSizePrice = this.ingredient.sizePrices.find(s => s.size === this.itemSize) || this.ingredient.sizePrices[0];
    return selectedSizePrice.price;
  }
}
